import React, { useState, useEffect } from 'react';
import { Globe, Plus, Trash2, CheckCircle, Shield, Loader2 } from 'lucide-react';
import { API } from '../../App';

export default function DomainsSection({ wsId }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newPolicy, setNewPolicy] = useState('route');
  const [error, setError] = useState('');

  const load = () => {
    if (!wsId) return;
    setLoading(true);
    API.get(`/api/workspaces/${wsId}/domains`)
      .then(r => setDomains(r.data.domains || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId]);

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    setError('');
    setAdding(true);
    try {
      await API.post(`/api/workspaces/${wsId}/domains`, { domain: newDomain.trim(), policy: newPolicy });
      setNewDomain('');
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed');
    }
    setAdding(false);
  };

  const verify = async (id) => {
    try {
      await API.post(`/api/workspaces/${wsId}/domains/${id}/verify`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Verification failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this domain?')) return;
    await API.delete(`/api/workspaces/${wsId}/domains/${id}`);
    load();
  };

  const togglePolicy = async (d) => {
    const next = d.policy === 'route' ? 'block' : 'route';
    await API.put(`/api/workspaces/${wsId}/domains/${d.id}`, { policy: next });
    load();
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading domains...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={newDomain} onChange={e => setNewDomain(e.target.value)}
          placeholder="example.com" onKeyDown={e => e.key === 'Enter' && addDomain()}
          className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
        <select value={newPolicy} onChange={e => setNewPolicy(e.target.value)}
          className="px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]">
          <option value="route">Route to workspace</option>
          <option value="block">Block signups</option>
        </select>
        <button onClick={addDomain} disabled={adding || !newDomain.trim()}
          className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 flex items-center gap-2 min-h-[44px]">
          <Plus className="w-4 h-4" /> Add Domain
        </button>
      </div>
      {error && <p className="text-[#EF4444] text-xs">{error}</p>}

      {domains.length === 0 ? (
        <div className="text-center py-8 text-[#A0A0A0]">
          <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No claimed domains yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {domains.map(d => (
            <div key={d.id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
              <Globe className="w-4 h-4 text-[#A0A0A0] flex-shrink-0" />
              <span className="text-sm font-mono flex-1">{d.domain}</span>
              <button onClick={() => togglePolicy(d)}
                className={`px-2 py-1 text-xs font-mono border ${d.policy === 'block' ? 'border-[#EF4444]/30 text-[#EF4444] bg-[#EF4444]/10' : 'border-[#FF4D00]/30 text-[#FF4D00] bg-[#FF4D00]/10'}`}>
                {d.policy}
              </button>
              {d.verified ? (
                <span className="flex items-center gap-1 text-xs text-[#22C55E]"><CheckCircle className="w-3 h-3" /> Verified</span>
              ) : (
                <button onClick={() => verify(d.id)} className="text-xs text-[#FF4D00] hover:underline flex items-center gap-1 min-h-[44px]">
                  <Shield className="w-3 h-3" /> Verify
                </button>
              )}
              <button onClick={() => remove(d.id)} className="p-2 text-[#A0A0A0] hover:text-[#EF4444] min-w-[44px] min-h-[44px] flex items-center justify-center">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
